/**
 * Single source of truth for every platform number the marketing site claims.
 *
 * WHY THIS FILE EXISTS
 * The site previously hardcoded coverage numbers in nine locale files and a
 * handful of components. They drifted from the platform and from each other —
 * "111 jurisdictions" was the sum of six hardcoded category counts and matched
 * nothing in the database. Every claim below carries the query or file that
 * verifies it. Change a number here, not in a locale file.
 *
 * FOLLOW-UP (fix list 1.2, WP2.4): replace these constants with values fetched
 * from a coverage endpoint that returns framework x jurisdiction x citation
 * depth. Until that endpoint exists, this file is the audit trail.
 *
 * All figures verified against the production Supabase project
 * `edyzytvoywcegqfuctto` on 2026-08-29 unless noted otherwise.
 */

export const METRICS_VERIFIED_ON = '2026-08-29';

/**
 * Frameworks offered in the platform's framework picker.
 * SELECT count(*) FROM frameworks_registry WHERE list_in_picker;  -> 60
 * (68 rows exist in total; 60 are listed and all 60 are enabled.)
 */
export const FRAMEWORKS_IN_PICKER = 60;

/**
 * Framework packages that are published AND have requirement content behind them.
 * SELECT count(*) FROM template_packages p
 *   WHERE p.is_public AND p.package_type = 'compliance_program'
 *     AND EXISTS (SELECT 1 FROM template_requirements r WHERE r.template_package_id = p.id);
 * -> 38  (47 packages are public across all package types.)
 */
export const PUBLISHED_FRAMEWORK_PACKAGES = 38;

/**
 * Requirements across every published package, and how many carry a citation
 * back to the source instrument (article, section, clause).
 * SELECT count(*), count(*) FILTER (WHERE source_ref <> '') FROM template_requirements;
 * -> 4083 total, 2074 cited
 */
export const TOTAL_REQUIREMENTS = 4083;
export const CITED_REQUIREMENTS = 2074;

/**
 * Jurisdictions in the RegGenome regulatory data feed.
 * SELECT count(*) FROM reggenome_jurisdictions;  -> 153 (147 accessible under entitlement)
 * Frameworks are actually MAPPED in far fewer, so the two numbers must always
 * be stated together. "111 jurisdictions" was never supported by anything.
 *
 * TWO DEFINITIONS, ONE PUBLISHED NUMBER
 * `jurisdictions_with_mapped_frameworks` is held at 17: distinct jurisdictions
 * across packages. The picker-listed query still in `query_sql`
 * (`count(distinct unnest(jurisdiction))` on `frameworks_registry` where
 * `list_in_picker`) returns 12. Source is `manual` so a refresh cannot flip
 * the published 17 to 12. Do not invent a third number. Do not set source
 * back to `derived` until a query is written that matches 17. UI reads the
 * hook, which serves the published 17.
 */
export const REGULATORY_DATA_JURISDICTIONS = 147;
export const JURISDICTIONS_WITH_MAPPED_FRAMEWORKS = 17;

/**
 * Regulatory domains in the feed.
 * SELECT count(*) FROM reggenome_domains;  -> 15
 * The site's old "27 regulatory domains" is unsupported — do not reintroduce it.
 */
export const REGULATORY_DOMAINS = 15;

/**
 * Source documents ingested from RegGenome.
 * SELECT count(*), count(DISTINCT lower(title)) FROM regulatory_documents;
 * -> 294 rows, 252 distinct titles (~42 near-duplicates pending the sync fix).
 * Quote the distinct figure, never the row count.
 */
export const REGULATORY_SOURCE_DOCUMENTS = 252;

/**
 * Integrations, counted from the platform repo, not the database.
 *
 * These are a compiled snapshot, not a live feed. The rows in
 * `platform_metrics` are `source = 'pushed'` and nothing writes them, so they
 * are unpublished. `reconcile()` will not accept an RPC value for these keys.
 * Do not print them through the live hook as if they update. Locale copy that
 * names 116 is the same snapshot, written by hand on purpose.
 *
 * Last counted from c2-app `develop` `src/data/integrationCatalog.ts` plus the
 * running library: 116 in the catalogue, 39 that actually connect. Re-count
 * with:
 *   git show develop:src/data/integrationCatalog.ts | grep -c "status: '"
 *
 * INTEGRATIONS_CLAIMED is the figure copy is allowed to show. The recommended
 * phrasing was "39 live, 116 in the library"; the flat catalogue number was
 * chosen instead. A prospect who filters the library by "not connected" sees
 * the gap. If that becomes a problem, the fix is COVERAGE_PHRASES.integrations,
 * not a smaller number here.
 */
/**
 * Specialized AI agents, and the orchestration stages they run in.
 *
 * The site said both 15 and 16 in roughly equal numbers: the Platform menu and
 * the FAQ said 16, /features, the architecture section and the platform tour
 * said 15. Nothing arbitrated, because the figure had no home in this file.
 * Founder decision Sep 1 2026: the number is 16. Anything that prints an agent
 * count imports it from here.
 */
export const AI_AGENTS = 16;
export const AGENT_ORCHESTRATION_STAGES = 5;

export const INTEGRATIONS_IN_CATALOG = 116;
export const INTEGRATIONS_LIVE = 39;
export const INTEGRATIONS_CLAIMED = 116;

/**
 * Framework families, and how many of each are offered in the picker.
 *
 * WHY THIS REPLACED THE SITE'S OWN CATEGORIES
 * The homepage coverage rail used to publish six marketing categories —
 * Security 20, Privacy 18, Financial 44, Industry 11, AI 6, Regional 12 —
 * whose counts came from a hand-maintained array in the component. They sum to
 * 111, which is the number this file's header already flags as matching nothing
 * in the platform. Once the rail moved next to the proof strip, the page
 * claimed 60+ frameworks and 111 frameworks about one scroll apart.
 *
 * These are the real families and the real picker counts, and they sum to
 * exactly FRAMEWORKS_IN_PICKER, so the two claims on the page now agree.
 *
 * SELECT framework_family,
 *        count(*) FILTER (WHERE is_enabled AND list_in_picker) AS in_picker
 *   FROM frameworks_registry GROUP BY framework_family ORDER BY in_picker DESC;
 *
 * Verified 2026-09-01. The FRAMEWORKS array in FrameworkCoverageMatrix stays as
 * the searchable list of named standards behind the typeahead; it is a superset
 * of the picker and must not be counted as a coverage claim.
 */
export const FRAMEWORK_FAMILIES = [
  { key: 'government', label: 'Government & defense', count: 12 },
  { key: 'financial_reporting', label: 'Financial reporting', count: 10 },
  { key: 'information_security', label: 'Information security', count: 10 },
  { key: 'privacy', label: 'Privacy', count: 9 },
  { key: 'industry_specific', label: 'Industry', count: 7 },
  { key: 'financial_crime', label: 'Financial crime', count: 6 },
  { key: 'operational_resilience', label: 'Operational resilience', count: 4 },
  { key: 'cloud_security', label: 'Cloud security', count: 2 },
] as const;

/** Guards the claim: the families must always account for the picker total. */
export const FRAMEWORK_FAMILY_TOTAL = FRAMEWORK_FAMILIES.reduce((n, f) => n + f.count, 0);

/**
 * Ready-made phrasings. Use these rather than writing a number into copy —
 * the wording carries the qualification that makes the number honest.
 */
export const COVERAGE_PHRASES = {
  frameworks: `${FRAMEWORKS_IN_PICKER} frameworks`,
  publishedPackages: `${PUBLISHED_FRAMEWORK_PACKAGES} published framework packages`,
  jurisdictions:
    `regulatory data covering ${REGULATORY_DATA_JURISDICTIONS} jurisdictions, ` +
    `with frameworks mapped in ${JURISDICTIONS_WITH_MAPPED_FRAMEWORKS} and more landing monthly`,
  integrations: `${INTEGRATIONS_CLAIMED} connectors and growing`,
  citations: `${CITED_REQUIREMENTS.toLocaleString('en-US')} requirements cited to their source instrument`,
} as const;

/* ---------------------------------------------------------------------------
 * LIVE METRICS
 *
 * Most constants above are the seed: first paint, and the fallback if the
 * fetch fails. The truth for those keys is `platform_metrics`, read through
 * get_public_platform_metrics() by src/lib/platformMetrics.ts.
 *
 * SNAPSHOT keys (connector counts) are not in that path. They stay at these
 * compiled values even if the RPC returns a number. Changing one of those is
 * a PR, because nothing in the database is allowed to publish them yet.
 *
 * When a live number here drifts from the platform it no longer matters much,
 * but keep it roughly current anyway: it is what a visitor sees for the first
 * few hundred milliseconds, and what they see permanently if Supabase is
 * blocked.
 * ------------------------------------------------------------------------- */

export interface FrameworkFamily {
  key: string;
  count: number;
  label?: string;
}

/** Seed values. Keys match the `platform_metrics.key` column exactly. */
export const METRIC_SEED = {
  frameworks_in_picker: FRAMEWORKS_IN_PICKER,
  published_framework_packages: PUBLISHED_FRAMEWORK_PACKAGES,
  total_requirements: TOTAL_REQUIREMENTS,
  cited_requirements: CITED_REQUIREMENTS,
  regulatory_data_jurisdictions: REGULATORY_DATA_JURISDICTIONS,
  jurisdictions_with_mapped_frameworks: JURISDICTIONS_WITH_MAPPED_FRAMEWORKS,
  regulatory_domains: REGULATORY_DOMAINS,
  regulatory_source_documents: REGULATORY_SOURCE_DOCUMENTS,
  integrations_in_catalog: INTEGRATIONS_IN_CATALOG,
  integrations_live: INTEGRATIONS_LIVE,
  ai_agents: AI_AGENTS,
  agent_orchestration_stages: AGENT_ORCHESTRATION_STAGES,
  framework_families: FRAMEWORK_FAMILIES as unknown as FrameworkFamily[],
};

export type MetricKey = keyof typeof METRIC_SEED;

/**
 * Keys that look like metrics but are compiled snapshots. The RPC must not
 * overwrite them: their `platform_metrics` rows are unpublished, `source` is
 * `pushed`, and nothing writes a new value.
 */
export const SNAPSHOT_METRIC_KEYS = [
  'integrations_in_catalog',
  'integrations_live',
] as const satisfies readonly MetricKey[];

export const isSnapshotMetric = (key: MetricKey): boolean =>
  (SNAPSHOT_METRIC_KEYS as readonly string[]).includes(key);

/**
 * Plausibility bounds, mirroring the ones on the table. Checked again on the
 * client because the cost of publishing a wrong number, on a site whose whole
 * argument is that its numbers survive inspection, is much higher than the cost
 * of publishing a slightly stale one.
 */
export const METRIC_BOUNDS: Partial<Record<MetricKey, [number, number]>> = {
  frameworks_in_picker: [20, 500],
  published_framework_packages: [10, 500],
  total_requirements: [500, 200_000],
  cited_requirements: [100, 200_000],
  regulatory_data_jurisdictions: [50, 300],
  jurisdictions_with_mapped_frameworks: [5, 300],
  regulatory_domains: [5, 200],
  regulatory_source_documents: [50, 100_000],
  integrations_in_catalog: [10, 2_000],
  integrations_live: [1, 2_000],
  ai_agents: [1, 200],
  agent_orchestration_stages: [1, 50],
};

/**
 * Phrasings built from whatever values are current. Use this rather than
 * writing a number into copy: the wording carries the qualification that makes
 * the number honest, and it stays correct as the number moves.
 */
export function coveragePhrases(m: typeof METRIC_SEED) {
  return {
    frameworks: `${m.frameworks_in_picker} frameworks`,
    publishedPackages: `${m.published_framework_packages} published framework packages`,
    jurisdictions:
      `regulatory data covering ${m.regulatory_data_jurisdictions} jurisdictions, ` +
      `with frameworks mapped in ${m.jurisdictions_with_mapped_frameworks} and more landing monthly`,
    // Snapshot constants, not hook values. A handwritten 116 must not look fetched.
    integrations: `${INTEGRATIONS_IN_CATALOG} connectors and growing`,
    integrationsSplit: `${INTEGRATIONS_LIVE} live of ${INTEGRATIONS_IN_CATALOG} in the library`,
    citations: `${m.cited_requirements.toLocaleString('en-US')} requirements cited to their source instrument`,
  } as const;
}
